// Parent Portal design tokens.
//
// Single source of truth for the redesigned surfaces so per-page hex
// values stop drifting. Imported by index.tsx and PortalPrimitives.
// Keep this file tiny — tokens only, no React, no MUI theme override.
//
// 2026-09 Bright Rainbow pass: the previous "muted premium banking"
// palette (steel-blue washes, near-white surfaces) is superseded here
// by a controlled bright rainbow. Field names and object shape are
// preserved so every existing consumer picks up the new colour
// without a rename or find-replace. Every value is a SOLID hex —
// no gradients, no alpha overlays.

export const pp = {
  color: {
    // Brand anchors.
    navy:      "#17324D",
    // `deepGreen` is the historical Parent Portal indigo brand
    // colour. Legacy name preserved to avoid a 30+ file rename;
    // the value is now a strong solid indigo per the 2026-09 pass.
    deepGreen: "#6366F1",
    // Semantic status accents. Present/Active/success → emerald;
    // late → amber; absent → rose. Values match the vivid palette
    // requested by the spec ("Blue #3B82F6, Emerald #22C55E, ...").
    green:     "#16A34A",
    greenSoft: "#22C55E",
    amber:     "#D97706",
    red:       "#E11D48",
    blue:      "#2563EB",
    sky:       "#38BDF8",
    aqua:      "#14B8A6",
    lavender:  "#7C3AED",
    text:      "#17324D",
    muted:     "#64748B",
    disabled:  "#94A3B8",
    border:    "#E3EAF1",
    borderStrong: "#CFDCE8",
    card:      "#FFFFFF",
    // Light neutral page background so bright cards stand out.
    pageBg:    "#F4F7FB",
  },
  surface: {
    // Full-fill solid card backgrounds. The previous pale steel-blue
    // washes (~#EAF2F8 etc.) are replaced by clearly-coloured medium
    // fills. Every existing `bgcolor: pp.surface.<tone>` reference
    // automatically becomes visibly colourful without per-site edits.
    mint:     "#C9EFE8",   // teal family — Attendance / calm surfaces
    blue:     "#CFE1FF",   // blue family — Classes / student header
    amber:    "#F9E1A3",   // amber family — warnings / financial hints
    red:      "#F7C5CC",   // coral family — absent / attention
    navy:     "#DDE6F5",   // deeper cool blue neutral
    grey:     "#EEF2F7",   // neutral secondary surface
    lavender: "#E5D8FA",   // purple family — Inbox / Homework
    aqua:     "#C9EFE8",   // alias for mint — teal family
  },
  radius: {
    sm: 10,
    md: 14,
    lg: 18,
    xl: 22,
    dialog: 18,
  },
  shadow: {
    card:      "0 6px 18px rgba(20,45,70,0.05)",
    cardHover: "0 10px 26px rgba(30,55,80,0.08)",
    hero:      "0 8px 24px rgba(30,55,80,0.06)",
  },
  transition: "all 180ms cubic-bezier(0.2, 0.8, 0.2, 1)",
} as const;

/**
 * Full-fill card variant map for the Parent Portal dashboard. Each
 * entry is a { bg, border, accent, text } quadruple — bg fills the
 * whole card, border is a slightly deeper solid edge, accent drives
 * icon tiles / buttons / big numbers, text is the primary heading
 * colour on that card.
 *
 * Every value is a solid hex — no gradients, no alpha overlays,
 * no decorative blobs. Consumers that need edge-to-edge colour
 * identity (Payments / This Week Classes / Inbox / Monthly
 * Attendance / Homework / Activity Calendar hero) read from this
 * map. Legacy consumers that read `pp.surface.<tone>` keep
 * working via the tokens above.
 */
export const PP_C = {
  blue:    { bg: "#CFE1FF", border: "#8AB5FF", accent: "#2563EB", text: "#17324D" },
  sky:     { bg: "#CDEFFF", border: "#8EDAF8", accent: "#0284C7", text: "#154661" },
  teal:    { bg: "#C9EFE8", border: "#83D9C9", accent: "#0F766E", text: "#164D47" },
  emerald: { bg: "#D3F0D8", border: "#93D9A3", accent: "#16A34A", text: "#214D2F" },
  indigo:  { bg: "#D9DCFF", border: "#A6AAF5", accent: "#4F46E5", text: "#373572" },
  purple:  { bg: "#E5D8FA", border: "#C4A8EF", accent: "#7C3AED", text: "#543A78" },
  amber:   { bg: "#F9E1A3", border: "#E8BF5E", accent: "#D97706", text: "#69470F" },
  orange:  { bg: "#F8CFAB", border: "#EEA06E", accent: "#EA580C", text: "#6C3916" },
  coral:   { bg: "#F7C5CC", border: "#EC909D", accent: "#E11D48", text: "#702936" },
  rose:    { bg: "#F7C5CC", border: "#EC909D", accent: "#E11D48", text: "#702936" },
  navy:    { bg: "#DDE6F5", border: "#B0C1DA", accent: "#17324D", text: "#17324D" },
} as const;

export type PPTone = keyof typeof PP_C;

/** Return a greeting for the local hour. Pure — safe to call in render. */
export const greetingForHour = (h: number = new Date().getHours()) =>
  h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
